/**
 * useCamera - カメラ機能を管理するカスタムフック
 */

import { useState, useCallback, useRef, useEffect } from 'react'
import type { ICameraService, CameraFrame, CameraDevice } from '../../../core/services/camera'
import { logger } from '../../../lib/logger'

interface UseCameraOptions {
  adapter?: ICameraService
  autoStart?: boolean
}

export function useCamera(options: UseCameraOptions = {}) {
  const { adapter, autoStart = false } = options
  
  // 状態管理
  const [isActive, setIsActive] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const [availableCameras, setAvailableCameras] = useState<CameraDevice[]>([])
  const [currentCamera, setCurrentCamera] = useState<CameraDevice | null>(null)
  const [zoomLevel, setZoomLevel] = useState(0.5) // デフォルトを広角に設定
  const [torchEnabled, setTorchEnabled] = useState(false)
  
  // カメラサービスのインスタンス
  const cameraService = useRef<ICameraService | null>(adapter || null)
  const videoRef = useRef<HTMLVideoElement | null>(null)

  // カメラの起動
  const startCamera = useCallback(async (videoElement?: HTMLVideoElement) => {
    logger.log('[useCamera] startCamera called, cameraService.current:', cameraService.current)
    if (!cameraService.current) {
      logger.error('[useCamera] Camera service not initialized')
      setError(new Error('Camera service not initialized'))
      return
    }

    const video = videoElement || videoRef.current
    logger.log('[useCamera] Video element:', video)
    if (!video) {
      logger.error('[useCamera] Video element not found')
      setError(new Error('Video element not found'))
      return
    }

    try {
      setIsLoading(true)
      setError(null)
      
      logger.log('[useCamera] Calling cameraService.start()...')
      await cameraService.current.start(video)
      logger.log('[useCamera] Camera started successfully')
      setIsActive(true)
      
      // 利用可能なカメラを取得
      const cameras = await cameraService.current.getAvailableCameras()
      logger.log('[useCamera] Available cameras:', cameras)
      setAvailableCameras(cameras)
      
      // 現在のカメラ情報を取得
      const capabilities = cameraService.current.getCapabilities()
      if (capabilities?.currentDevice) {
        setCurrentCamera(capabilities.currentDevice)
        
        // 広角ズーム（0.5x）を設定（利用可能な場合）
        if (capabilities.currentDevice.capabilities?.zoom) {
          const { min } = capabilities.currentDevice.capabilities.zoom
          if (min <= 0.5) {
            await cameraService.current.setZoom(0.5)
            setZoomLevel(0.5)
          }
        }
      }
    } catch (err) {
      logger.error('[useCamera] Failed to start camera:', err)
      setError(err instanceof Error ? err : new Error('Failed to start camera'))
      setIsActive(false)
    } finally {
      setIsLoading(false)
    }
  }, [])

  // カメラの停止
  const stopCamera = useCallback(() => {
    if (!cameraService.current) return
    
    try {
      cameraService.current.stop()
      setIsActive(false)
      setCurrentCamera(null)
      setAvailableCameras([])
    } catch (err) {
      logger.error('[useCamera] Failed to stop camera:', err)
      setError(err instanceof Error ? err : new Error('Failed to stop camera'))
    }
  }, [])

  // 画像のキャプチャ
  const captureImage = useCallback(async (): Promise<CameraFrame | null> => {
    logger.log('[useCamera] captureImage called, isActive:', isActive, 'cameraService:', !!cameraService.current)
    
    if (!cameraService.current || !isActive) {
      logger.error('[useCamera] Cannot capture - cameraService:', !!cameraService.current, 'isActive:', isActive)
      setError(new Error('Camera is not active'))
      return null
    }

    try {
      setError(null)
      logger.log('[useCamera] Calling cameraService.capture()...')
      const frame = await cameraService.current.capture()
      logger.log('[useCamera] Capture successful, frame:', !!frame)
      return frame
    } catch (err) {
      logger.error('[useCamera] Failed to capture image:', err)
      setError(err instanceof Error ? err : new Error('Failed to capture image'))
      return null
    }
  }, [isActive])

  // カメラの切り替え
  const selectCamera = useCallback(async (deviceId: string) => {
    if (!cameraService.current) return
    
    try {
      setIsLoading(true)
      setError(null)
      
      await cameraService.current.selectCamera(deviceId)
      
      // 新しいカメラ情報を取得
      const capabilities = cameraService.current.getCapabilities()
      if (capabilities?.currentDevice) {
        setCurrentCamera(capabilities.currentDevice)
      }
    } catch (err) {
      logger.error('[useCamera] Failed to select camera:', err)
      setError(err instanceof Error ? err : new Error('Failed to select camera'))
    } finally {
      setIsLoading(false)
    }
  }, [])

  // カメラの前後切り替え
  const switchCamera = useCallback(async () => {
    if (!cameraService.current) return
    
    try {
      setIsLoading(true)
      setError(null)
      
      await cameraService.current.switchCamera()
      
      // 新しいカメラ情報を取得
      const capabilities = cameraService.current.getCapabilities()
      if (capabilities?.currentDevice) {
        setCurrentCamera(capabilities.currentDevice)
      }
    } catch (err) {
      logger.error('[useCamera] Failed to switch camera:', err)
      setError(err instanceof Error ? err : new Error('Failed to switch camera'))
    } finally {
      setIsLoading(false)
    }
  }, [])

  // ズーム設定
  const setZoom = useCallback(async (factor: number) => {
    logger.log('[useCamera] setZoom called with factor:', factor)
    if (!cameraService.current) {
      logger.error('[useCamera] Camera service not available')
      return
    }
    
    try {
      // 即座に状態を更新（UIの応答性向上）
      setZoomLevel(factor)
      
      // カメラAPIにズーム値を適用
      await cameraService.current.setZoom(factor)
      logger.log('[useCamera] Zoom set successfully to:', factor)
    } catch (err) {
      logger.error('[useCamera] Failed to set zoom:', err)
      setError(err instanceof Error ? err : new Error('Failed to set zoom'))
      // エラー時はデフォルト値に戻す
      setZoomLevel(1)
    }
  }, [])

  // トーチ（フラッシュ）設定
  const setTorch = useCallback(async (enabled: boolean) => {
    if (!cameraService.current) return
    
    try {
      await cameraService.current.setTorch(enabled)
      setTorchEnabled(enabled)
    } catch (err) {
      logger.error('[useCamera] Failed to set torch:', err)
      setError(err instanceof Error ? err : new Error('Failed to set torch'))
    }
  }, [])

  // 自動起動
  // autoStart効果 - 真のマウント/アンマウント時のみ実行
  useEffect(() => {
    if (autoStart && videoRef.current && cameraService.current) {
      void startCamera(videoRef.current)
    }
    
    // クリーンアップ - コンポーネントが本当にアンマウントされる時のみ
    return () => {
      // 直接cameraServiceを停止（stopCamera関数への依存を避ける）
      if (cameraService.current?.isActive()) {
        logger.log('[useCamera] Cleanup: stopping camera on unmount')
        cameraService.current.stop()
      }
    }
    // startCameraとstopCameraは安定した関数なので依存配列から除外
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoStart])

  return {
    // 状態
    isActive,
    isLoading,
    error,
    availableCameras,
    currentCamera,
    zoomLevel,
    torchEnabled,
    
    // アクション
    startCamera,
    stopCamera,
    captureImage,
    selectCamera,
    switchCamera,
    setZoom,
    setTorch,
    
    // refs
    videoRef,
    setCameraService: (service: ICameraService) => {
      cameraService.current = service
    }
  }
}